import { useEffect } from "react";
import { optimizeImage } from "@/lib/imageUtils";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Star, Shield, Clock, MapPin, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { ContactForm } from "@/components/ContactForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { GoogleMapsEmbed } from "@/components/GoogleMapsEmbed";

const Index = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://miros-haushaltsaufloesung.de/#organization",
    name: "Miros Haushaltsauflösung",
    url: "https://miros-haushaltsaufloesung.de/",
    logo: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/698f5d79c6490f2e4d428a9e.png",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+4915253181061",
      contactType: "customer service",
    },
    sameAs: [
      "https://www.google.com/maps/place/Miros+Haushaltsaufl%C3%B6sung/@52.7890372,12.4843482,9z/data=!3m1!4b1!4m6!3m5!1s0x23bf4a60473c084d:0x8a12d3b416c3d36a!8m2!3d52.7890372!4d12.4843482!16s%2Fg%2F11w12b1_n0",
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://miros-haushaltsaufloesung.de/#website",
    url: "https://miros-haushaltsaufloesung.de/",
    name: "Miros Haushaltsauflösung",
    publisher: {
      "@id": "https://miros-haushaltsaufloesung.de/#organization",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Wie schnell bekommen wir einen Termin?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Besichtigungstermine sind oft innerhalb von 24 Stunden möglich. Die eigentliche Entrümpelung findet meist in derselben Woche statt. Bei dringenden Fällen organisieren wir auch kurzfristigere Termine.",
        },
      },
      {
        "@type": "Question",
        name: "Was kostet eine Entrümpelung?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Das hängt von Größe, Stockwerk und Zugänglichkeit ab. Eine typische 3-Zimmer-Wohnung liegt zwischen 800 und 1.500 Euro. Nach der kostenlosen Besichtigung erhalten Sie einen verbindlichen Festpreis.",
        },
      },
      {
        "@type": "Question",
        name: "Muss ich vorher irgendetwas tun?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Nein. Sie müssen nur persönliche Dinge wie Dokumente, Fotos oder Schmuck in Sicherheit bringen und uns sagen, was weg soll. Den Rest übernehmen wir vollständig.",
        },
      },
      {
        "@type": "Question",
        name: "Übernehmen Sie auch die Entsorgung?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja, komplett. Wir sortieren vor Ort: Verwertbares geht an Second-Hand-Läden oder Recyclingbetriebe, Sondermüll wird fachgerecht entsorgt.",
        },
      },
      {
        "@type": "Question",
        name: "Führen Sie auch Endreinigungen durch?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja. Wir übergeben die Räume besenrein oder auf Wunsch grundgereinigt.",
        },
      },
      {
        "@type": "Question",
        name: "Erkennen Sie Wertgegenstände?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja. Unser Team erkennt Antiquitäten, Sammlerstücke und andere Wertsachen und rechnet sie transparent gegen die Kosten an.",
        },
      },
      {
        "@type": "Question",
        name: "Was passiert, wenn beim Transport etwas beschädigt wird?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Wir sind vollständig betriebshaftpflichtversichert. Sollte trotz aller Sorgfalt ein Schaden entstehen, übernimmt unsere Versicherung die Kosten.",
        },
      },
    ],
  };

  const schemas = [organizationSchema, websiteSchema, faqSchema];

  return (
    <>
      <SEOHead
        title="Haushaltsauflösung zum Festpreis in Berlin & Umgebung"
        description="Bewährt in über 500 Haushaltsauflösungen in Berlin & Umgebung. Familienbetrieb mit Festpreisgarantie und fachgerechter Entsorgung. Jetzt anfragen!"
        canonical="https://miros-haushaltsaufloesung.de/"
        schemaJson={schemas}
        ogImage="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c799.jpg"
      />
      <div className="flex flex-col w-full">
        {/* HERO SECTION */}
        <section className="relative w-full pt-32 pb-20 md:pt-40 md:pb-32 flex items-center justify-center bg-[#0c2340] text-white overflow-hidden">
          <div className="container relative z-20 mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
              {/* Left Column: Text & CTAs */}
              <div className="text-left space-y-8">
                {/* Trust Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/10 text-white border border-white/20 backdrop-blur-sm max-w-full">
                  <Star className="h-3 w-3 md:h-4 md:w-4 fill-yellow-400 text-yellow-400 shrink-0" />
                  <span className="text-xs md:text-sm font-semibold tracking-wide uppercase truncate md:whitespace-normal">
                    Top Bewertet in Berlin (5.0/5)
                  </span>
                </div>

                {/* H1 Headline */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black text-white leading-[1.1] tracking-tight">
                  Zuverlässige Haushaltsauflösung{" "}
                  <br className="hidden md:block" />
                  mit Festpreisgarantie.
                </h1>

                {/* Subheadline */}
                <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-xl font-medium leading-relaxed">
                  Bewährt in über 500 Haushaltsauflösungen.{" "}
                  <br className="hidden md:block" />
                  Wir übergeben besenrein, pünktlich und ohne versteckte Kosten.
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="w-full sm:w-auto h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-lg hover:shadow-xl"
                      >
                        Kostenlose Besichtigung anfragen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <a
                    href="tel:015253181061"
                    className="w-full sm:w-auto h-14 px-8 text-lg font-bold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-md shadow-sm inline-flex items-center justify-center border transition-colors"
                  >
                    01525 3181061 anrufen
                  </a>
                </div>

                {/* Value Props & Extra Trust */}
                <div className="pt-6 border-t border-white/20">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-white text-sm md:text-base font-medium">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                      <span>100% Familienbetrieb</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                      <span>Garantierter Festpreis</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                      <span>Fachgerechte Entsorgung</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                      <span>Besenreine Übergabe</span>
                    </div>
                  </div>

                  {/* Additional Trust Logos/Text */}
                  <div className="mt-8 flex items-center gap-4 text-sm text-white/80 font-medium">
                    <div className="flex -space-x-2">
                      {[
                        "https://i.pravatar.cc/100?img=11",
                        "https://i.pravatar.cc/100?img=32",
                        "https://i.pravatar.cc/100?img=44",
                      ].map((img, i) => (
                        <div
                          key={i}
                          className="w-8 h-8 rounded-full border-2 border-[#0c2340] bg-secondary overflow-hidden"
                        >
                          <img
                            src={img}
                            alt="Zufriedener Kunde"
                            width="32"
                            height="32"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                    <p>
                      Empfohlen von über{" "}
                      <strong className="text-white">
                        500+ zufriedenen Kunden
                      </strong>{" "}
                      in Berlin.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="relative mx-auto w-full max-w-lg lg:max-w-none mt-8 lg:mt-0">
                {/* Decorative blob/shape behind */}
                <div className="absolute -inset-4 bg-secondary/50 rounded-full blur-3xl -z-10"></div>

                <div className="relative rounded-sm overflow-hidden shadow-2xl border border-border">
                  <img
                    src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c791.jpg"
                    alt="Miros Haushaltsauflösung Team"
                    width="800"
                    height="600"
                    className="w-full h-auto object-cover aspect-[4/3] lg:aspect-auto lg:h-[600px]"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>

                {/* Floating Trust Element */}
                <div className="absolute -bottom-4 -left-2 md:-bottom-6 md:-left-10 bg-background p-3 md:p-4 rounded-sm shadow-xl border border-border flex items-center gap-3 md:gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300 max-w-[90%]">
                  <div className="bg-green-100 p-2 md:p-3 rounded-full shrink-0">
                    <Shield className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs md:text-sm font-bold text-foreground">
                      Zertifizierter Betrieb
                    </div>
                    <div className="text-[10px] md:text-xs text-muted-foreground">
                      Mit Haftpflichtversicherung
                    </div>
                  </div>
                </div>

                {/* Floating 2 */}
                <div className="absolute -top-4 -right-2 md:-top-6 md:-right-8 bg-background p-3 md:p-4 rounded-sm shadow-xl border border-border flex items-center gap-2 md:gap-3 animate-in fade-in slide-in-from-top-4 duration-700 delay-500">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className="h-3 w-3 md:h-4 md:w-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  <div className="text-xs md:text-sm font-bold text-foreground">
                    5.0 auf Google
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EMPATHY SECTION */}
        <section className="py-20 md:py-28 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                Wir wissen, wie schwer diese Zeit für Sie ist
              </h2>
              <div className="space-y-6 text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed text-left md:text-center max-w-3xl mx-auto">
                <p>
                  Nach einem Todesfall oder Umzug haben Sie bereits genug um die
                  Ohren. Behördengänge, Papierkram, wichtige Entscheidungen. Und
                  dann steht noch die Wohnungsauflösung an.
                </p>
                <p>
                  Vielleicht wohnen Sie nicht mal in Berlin. Die Kündigungsfrist
                  läuft, jeden Monat kostet die leere Wohnung Geld, und sich
                  alleine durch die Sachen eines geliebten Menschen zu arbeiten
                  ist kaum zu bewältigen.
                </p>
              </div>

              <div className="mt-12 max-w-4xl mx-auto text-center">
                <div className="inline-block p-4 md:p-6 border-2 border-accent rounded-sm bg-accent/5 shadow-sm mb-10">
                  <p className="text-xl md:text-2xl font-bold text-foreground">
                    Genau für diese Situation sind wir da.
                  </p>
                </div>
                <div className="space-y-4 text-lg text-muted-foreground text-left md:text-center mb-10 max-w-3xl mx-auto">
                  <p>
                    Als Familienbetrieb mit jahrelanger Erfahrung in Berlin
                    nehmen wir Ihnen diese Last ab, einfühlsam und
                    professionell. Sie müssen nicht dabei sein. Wir kümmern uns
                    um alles, von den engen Treppenhäusern in Kreuzberg bis zu
                    den Gründerzeitvillen in Charlottenburg.
                  </p>
                  <p>
                    Mit festen Preisen, der richtigen Ausrüstung und dem Blick
                    dafür, was sich noch verwerten lässt, machen wir aus dem
                    vollen Raum wieder freien Raum. Und aus einer Belastung
                    einen abgehakten Punkt.
                  </p>
                </div>

                {/* Bullet points */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 text-left max-w-3xl mx-auto mb-10">
                  {[
                    "Wertanrechnung",
                    "Kostenlose Erstberatung",
                    "Vollständige Absicherung im Schadensfall",
                    "Großes Team für schnelle Abwicklung",
                    "Jahrelange Branchenerfahrung",
                    "Angebot innerhalb 24h erhalten",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-500 shrink-0" />
                      <span className="font-medium text-foreground text-lg">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Before/After Slider */}
                <div className="max-w-4xl mx-auto rounded-sm overflow-hidden border border-border shadow-md">
                  <BeforeAfterSlider
                    beforeImage="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a9011b2e956bc24f8cdbe.jpg"
                    afterImage="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c77b.jpg"
                  />
                </div>
              </div>

              {/* ANIMATED CTA SECTION */}
              <div
                className="mt-20 max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-1000 fill-mode-both"
                style={{ animationDelay: "200ms" }}
              >
                <div className="flex flex-col md:flex-row bg-primary rounded-sm overflow-hidden shadow-2xl border border-border">
                  {/* Left Side: Text and CTAs */}
                  <div className="flex-1 p-6 sm:p-10 md:p-12 flex flex-col justify-center text-left">
                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary-foreground mb-8 leading-tight">
                      Kostenlose Besichtigung –
                      <br className="hidden md:block" />
                      klare Einschätzung, fester
                      <br className="hidden md:block" />
                      Preis, schnelle Unterstützung
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            size="lg"
                            className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                          >
                            Kostenfreies Angebot{" "}
                            <span className="ml-2 font-normal">→</span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                          <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                            <DialogHeader className="mb-5">
                              <DialogTitle className="text-xl sm:text-2xl font-bold">
                                Kostenloses Angebot anfordern
                              </DialogTitle>
                              <DialogDescription className="text-sm sm:text-base">
                                Hinterlassen Sie uns Ihre Details und wir melden
                                uns umgehend bei Ihnen.
                              </DialogDescription>
                            </DialogHeader>
                            <ContactForm />
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button
                        size="lg"
                        variant="outline"
                        className="h-14 px-8 text-lg font-bold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-md shadow-md transition-transform hover:scale-105"
                        asChild
                      >
                        <a href="tel:015253181061">
                          <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                        </a>
                      </Button>
                    </div>
                  </div>

                  {/* Right Side: Image */}
                  <div className="w-full md:w-2/5 min-h-[300px] md:min-h-full relative">
                    <img
                      src={optimizeImage(
                        "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a181eb301d4357a45.jpg",
                        600,
                      )}
                      alt="Kostenlose Besichtigung"
                      width="600"
                      height="800"
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PRICING SECTION */}
        <section id="kosten" className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="space-y-8">
                <div>
                  <div className="text-primary font-bold tracking-wide uppercase mb-2">
                    Transparente Preise
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                    Was kostet eine{" "}
                    <span className="text-primary">Haushaltsauflösung</span> in
                    Berlin?
                  </h2>
                </div>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Wahrscheinlich die Frage, die Ihnen schon seit der
                    Überschrift auf der Zunge brennt:{" "}
                    <strong>
                      Was kostet eine Haushaltsauflösung in Berlin eigentlich?
                    </strong>{" "}
                    Bei Miros Haushaltsauflösung starten unsere Preise fair
                    kalkuliert. Natürlich kann es je nach Größe und Aufwand
                    variieren, aber unser Festpreis bleibt in jedem Fall
                    transparent.
                  </p>

                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Beispiel:</strong> Sie
                      haben eine Zweizimmerwohnung mit Keller in Berlin, die
                      entrümpelt werden muss. Anhand Ihrer Fotos und
                      Informationen kalkulieren wir genau und nennen Ihnen einen
                      fairen Festpreis. In diesem Preis sind sämtliche Kosten
                      für Abbau, Transport, Entsorgung und Personal enthalten.
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">
                        Warum unsere Preise fair sind:
                      </strong>{" "}
                      Wir arbeiten effizient. Wir sind eingespielt, kennen die
                      Anlaufstellen in Berlin und müssen keine Zeit mit
                      unnötigen Wegen verschwenden. Effizienz spart Kosten – für
                      uns und damit für Sie.
                    </div>
                  </div>

                  <p className="font-medium text-foreground">
                    <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei
                    und unverbindlich bei uns an. Anhand Ihrer Angaben stellen
                    wir Ihnen ein transparentes Angebot für Ihre
                    Haushaltsauflösung in Berlin zusammen.
                  </p>
                </div>
              </div>

              {/* Right Column: Pricing Box */}
              <div className="relative w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="absolute -inset-4 bg-primary/10 rounded-sm transform rotate-2 z-0"></div>
                <div className="relative z-10 bg-primary text-primary-foreground p-8 md:p-10 rounded-sm shadow-xl flex flex-col items-center text-center">
                  <h3 className="text-2xl font-bold mb-4">
                    Haushaltsauflösung
                  </h3>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-xl font-medium">ab</span>
                    <span className="text-5xl md:text-6xl font-heading font-black text-accent">
                      199 €
                    </span>
                  </div>

                  <p className="font-bold mb-6 opacity-90">abhängig von</p>

                  <ul className="space-y-4 mb-8 w-full text-left">
                    {[
                      "Fläche in QM",
                      "Menge des Hausrats",
                      "Zusatzleistungen (z.B. Bodenbeläge)",
                      "Stockwerk & Aufzug",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="w-full h-14 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Genaue Kosten anfragen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto text-foreground">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>

                  <p className="text-sm mt-4 opacity-80">
                    *kostenfrei und unverbindlich
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY TRUST US SECTION */}
        <section
          id="warum-vertrauen"
          className="py-20 md:py-32 bg-secondary/30"
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8 items-start">
              {/* Left Column: Text & Features */}
              <div className="lg:col-span-2">
                <div className="max-w-2xl mb-12">
                  <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-bold text-foreground mb-6">
                    Warum Auftraggeber uns vertrauen
                  </h2>
                  <p className="text-base sm:text-lg text-muted-foreground">
                    Wir wissen, dass eine Haushaltsauflösung Vertrauenssache
                    ist. Deshalb setzen wir auf Transparenz, Zuverlässigkeit und
                    höchste Professionalität.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    {
                      title: "100% Festpreisgarantie",
                      desc: "Keine versteckten Kosten, keine Nachverhandlungen. Sie zahlen genau das, was wir vorher vereinbart haben.",
                      icon: <Shield className="h-6 w-6" />,
                    },
                    {
                      title: "Besenreine Übergabe",
                      desc: "Wir hinterlassen die Räumlichkeiten besenrein, sodass Sie diese direkt an den Vermieter oder Käufer übergeben können.",
                      icon: <CheckCircle2 className="h-6 w-6" />,
                    },
                    {
                      title: "Fachgerechte Entsorgung",
                      desc: "Wir trennen Müll strikt und entsorgen umweltgerecht bei zertifizierten Fachbetrieben in Berlin.",
                      icon: <Star className="h-6 w-6" />,
                    },
                    {
                      title: "Absolute Diskretion",
                      desc: "Wir arbeiten leise, unauffällig und respektieren Ihre Privatsphäre sowie die der Nachbarn.",
                      icon: <Shield className="h-6 w-6" />,
                    },
                    {
                      title: "Termintreue Umsetzung",
                      desc: "Was wir versprechen, halten wir. Wir räumen termingerecht und zuverlässig zum vereinbarten Datum.",
                      icon: <Clock className="h-6 w-6" />,
                    },
                    {
                      title: "Familienbetrieb mit Herz",
                      desc: "Als Inhabergeführter Betrieb kümmern wir uns persönlich um Ihr Anliegen – von der ersten Besichtigung bis zur Schlüsselübergabe.",
                      icon: <Shield className="h-6 w-6" />,
                    },
                  ].map((feature, idx) => (
                    <div
                      key={idx}
                      className="bg-background p-6 rounded-sm border border-border shadow-sm flex flex-col items-start text-left hover:shadow-md transition-shadow"
                    >
                      <div className="w-10 h-10 bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-4">
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-bold mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {feature.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column: Contact Person */}
              <div className="lg:col-span-1 lg:sticky lg:top-32 flex flex-col items-center bg-background p-8 rounded-sm border border-border shadow-lg">
                <div className="w-full rounded-sm overflow-hidden mb-6 border border-border/50">
                  <img
                    src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900e659bec99fc9f3afb.jpg"
                    alt="Miroslav Ozegovic"
                    width="400"
                    height="500"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-cover aspect-[4/5]"
                  />
                </div>
                <div className="text-center w-full">
                  <p className="text-sm text-muted-foreground uppercase tracking-wider font-bold mb-1">
                    Ihr Ansprechpartner
                  </p>
                  <p className="text-2xl font-heading font-bold text-foreground mb-6">
                    Miroslav Ozegovic
                  </p>
                  <Button
                    size="lg"
                    className="w-full h-14 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* REVIEWS SECTION */}
        <section
          id="bewertungen"
          className="py-20 md:py-32 bg-primary text-primary-foreground"
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6">
                Was unsere Kunden sagen
              </h2>
              <p className="text-lg opacity-90">
                Echte Bewertungen von echten Kunden auf unserem Google Profil.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {[
                {
                  text: "Sehr professionell, pünktlich und schnell. Die Wohnung wurde komplett besenrein hinterlassen. Absolute Empfehlung!",
                  author: "Kunde aus Pankow",
                  img: "https://i.pravatar.cc/100?img=11",
                },
                {
                  text: "Herr Ozegovic und sein Team haben tolle Arbeit geleistet. Der Festpreis wurde genau eingehalten. Keine versteckten Kosten.",
                  author: "Kundin aus Charlottenburg",
                  img: "https://i.pravatar.cc/100?img=32",
                },
                {
                  text: "Schnelle Terminvergabe und reibungsloser Ablauf bei unserer Büroauflösung. Sehr freundlicher Kontakt.",
                  author: "Unternehmen aus Spandau",
                  img: "https://i.pravatar.cc/100?img=44",
                },
              ].map((review, idx) => (
                <div
                  key={idx}
                  className="bg-primary-foreground/10 p-8 rounded-sm backdrop-blur-sm flex flex-col"
                >
                  <div className="flex gap-1 mb-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className="h-5 w-5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="text-lg font-medium mb-8 leading-relaxed flex-grow">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-4 mt-auto">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary-foreground/20 shrink-0">
                      <img
                        src={review.img}
                        alt={review.author}
                        width="48"
                        height="48"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="font-bold opacity-90">{review.author}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button
                size="lg"
                className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-lg hover:shadow-xl"
                asChild
              >
                <a
                  href="https://g.page/r/CWrTwxa00xKKEBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Auf Google bewerten
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* BEREAVEMENT SECTION */}
        <section id="todesfall" className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Haushaltsauflösung nach Todesfall
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Das Elternhaus auflösen, während man noch trauert. Das ist
                    eine der schwersten Aufgaben, die es gibt.
                  </p>
                  <p>
                    Wir gehen dabei so vor, wie wir es uns selbst wünschen
                    würden: ruhig, diskret und respektvoll. Unsere Mitarbeiter
                    arbeiten unauffällig, ohne große Logos oder unnötigen Lärm.
                    Die Nachbarn bemerken kaum etwas.
                  </p>
                </div>

                <div className="bg-background p-6 md:p-8 rounded-sm border border-border shadow-sm">
                  <h3 className="text-xl font-bold text-foreground mb-4">
                    So können Sie sich vorbereiten, wenn Sie möchten
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Keine Pflicht, aber manchen hilft es, vorab ein paar kleine
                    Dinge zu erledigen:
                  </p>
                  <ul className="space-y-3">
                    {[
                      "Persönliche Erinnerungsstücke wie Fotos und Dokumente in einem separaten Karton sichern",
                      "Aktuellen Strom und Gasstand notieren",
                      "Gegenstände, die sicher weg sollen, mit einem Klebepunkt markieren",
                      "Hausschlüssel beim Hausverwalter vorab abholen, falls nötig",
                    ].map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-muted-foreground"
                      >
                        <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 font-bold text-foreground text-lg">
                    Den Rest übernehmen wir.
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Kostenfreies Angebot{" "}
                        <span className="ml-2 font-normal">→</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary rounded-md shadow-sm transition-transform hover:scale-105"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform rotate-2 z-0"></div>
                <img
                  src={optimizeImage(
                    "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c799.jpg",
                    600,
                  )}
                  alt="Haushaltsauflösung nach Todesfall"
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* INSURANCE SECTION */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Image */}
              <div className="relative w-full order-2 lg:order-1">
                <div className="absolute -inset-4 bg-secondary/50 rounded-sm transform -rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a967e20d627a9dcbb.jpg"
                  alt="Vollversichert und für schwierige Fälle gerüstet"
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>

              {/* Right Column: Text */}
              <div className="space-y-8 order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Vollversichert. Auch in schwierigen Fällen.
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Ein Kratzer im Treppenhaus, eine beschädigte Wand beim
                    Transport: Solche Dinge können passieren. Deshalb sind wir
                    vollständig betriebshaftpflichtversichert. Sie und Ihre
                    Nachbarn sind zu 100 Prozent abgesichert, ohne dass Sie sich
                    darum kümmern müssen.
                  </p>
                  <p>
                    Wir übernehmen auch Aufträge, die andere ablehnen. Wenn
                    jahrelange Sammelleidenschaft zu unübersichtlichen Zuständen
                    geführt hat, ist das für uns kein Problem. Unser Team ist
                    für solche Situationen ausgebildet und kommt mit der nötigen
                    Ausrüstung, inklusive Atemschutz und Ozon-Generatoren gegen
                    Gerüche.
                  </p>
                </div>

                <div className="bg-primary/5 p-6 md:p-8 rounded-sm border border-primary/10 shadow-sm">
                  <p className="font-bold text-foreground text-xl">
                    Sie müssen sich für nichts schämen. Wir haben schon alles
                    gesehen.
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Kostenfreies Angebot{" "}
                        <span className="ml-2 font-normal">→</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary rounded-md shadow-sm transition-transform hover:scale-105"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ECO DISPOSAL SECTION */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Umweltgerechte Entsorgung inklusive
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Nicht alles muss in die Verbrennung. Wir sortieren bereits
                    in Ihrer Wohnung vor: Was noch zu gebrauchen ist, kommt zu
                    zertifizierten Berliner Recyclinghöfen. Massivholzmöbel,
                    funktionierende Elektrogeräte, Metalle, Textilien alles wird
                    so entsorgt oder weitergegeben, wie es am sinnvollsten ist.
                  </p>
                  <p>
                    Das spart Ihnen Kosten und schont die Umwelt. Und Sie müssen
                    sich um nichts davon kümmern.
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Kostenfreies Angebot{" "}
                        <span className="ml-2 font-normal">→</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary rounded-md shadow-sm transition-transform hover:scale-105"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a9016724990971212353e.jpg"
                  alt="Umweltgerechte Entsorgung"
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES SECTION */}
        <section id="leistungen" className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-6">
                Professionelle Entrümpelung & Haushaltsauflösung in Berlin
              </h2>
              <p className="text-lg text-muted-foreground">
                Ob private Wohnungsauflösung, gewerbliche Büroauflösung oder
                diskrete Nachlassverwertung – wir bieten Ihnen maßgeschneiderte
                Lösungen für jede Herausforderung. Verlassen Sie sich auf unsere
                jahrelange Erfahrung, Festpreisgarantie und besenreine Übergabe.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Entrümpelung",
                  link: "/entruempelung-berlin",
                  img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a967e20d627a9db4d.jpg",
                  desc: "Schnelle und fachgerechte Entrümpelung von Kellern, Dachböden, Garagen und ganzen Häusern in Berlin. Wir befreien Sie von Altlasten, Sperrmüll und Unrat – effizient, diskret und mit umweltgerechter Entsorgung auf zertifizierten Recyclinghöfen.",
                },
                {
                  title: "Haushaltsauflösung",
                  link: "/haushaltsaufloesung-berlin",
                  img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a109a1ab49dc13ffb.jpg",
                  desc: "Komplette Haushaltsauflösungen zum Festpreis. Wir räumen Häuser und Wohnungen vom Keller bis zum Dachboden. Inklusive Demontage von Möbeln, Entfernung von Bodenbelägen und besenreiner Übergabe an Sie oder Ihren Vermieter.",
                },
                {
                  title: "Wohnungsauflösung",
                  link: "/wohnungsaufloesung-berlin",
                  img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a181eb301d4357a32.jpg",
                  desc: "Stressfreie Wohnungsauflösung bei Umzug ins Pflegeheim oder ins Ausland. Wir übernehmen die komplette Räumung, sortieren Wertstoffe sorgfältig aus und kümmern uns um alle anfallenden Arbeiten, damit Sie den Kopf frei haben.",
                },
                {
                  title: "Nachlassverwertung",
                  link: "/nachlassverwertung-berlin",
                  img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a9011ee187be68947e2b1.jpg",
                  desc: "Einfühlsame und diskrete Räumung nach einem Todesfall. Wir sichten den Nachlass mit größter Sorgfalt, rechnen verwertbare Gegenstände fair an und übergeben die Räumlichkeiten besenrein und termingerecht.",
                },
                {
                  title: "Büroauflösung",
                  link: "/bueroaufloesung-berlin",
                  img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a9016967e20d627a9d3de.jpg",
                  desc: "Professionelle Büroauflösung und Aktenvernichtung für Unternehmen in Berlin. Wir demontieren Büromöbel, entfernen IT-Infrastruktur fachgerecht und hinterlassen die Gewerberäume übergabefertig für den Nachmieter.",
                },
                {
                  title: "Geschäftsauflösung",
                  link: "/geschaeftsaufloesung-berlin",
                  img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a90165ff04dbbc3c00b0e.jpg",
                  desc: "Zuverlässige Räumung von Ladenlokalen, Lagern und Werkstätten. Wir unterstützen Sie bei der Geschäftsaufgabe oder Standortverlagerung mit strukturierten Abläufen, damit Sie sich schnell wieder auf Ihr Kerngeschäft konzentrieren können.",
                },
              ].map((service, idx) => (
                <div
                  key={idx}
                  className="bg-background rounded-sm shadow-sm border border-border hover:shadow-md transition-all group overflow-hidden flex flex-col"
                >
                  <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                    <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors z-10 duration-300"></div>
                    <img
                      src={service.img}
                      alt={service.title}
                      width="400"
                      height="250"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 md:p-8 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold mb-3 text-foreground">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground mb-6 text-sm leading-relaxed flex-grow">
                      {service.desc}
                    </p>
                    <a
                      href={service.link}
                      className="text-primary font-bold inline-flex items-center hover:underline mt-auto"
                    >
                      Mehr erfahren <span className="ml-2">→</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMMERCIAL SECTION */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Gewerberäume und Büros
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Ob Umzug, Insolvenz oder Praxisauflösung: Wir räumen
                    Gewerberäume termingerecht und ohne unnötige Unterbrechung
                    des laufenden Betriebs.
                  </p>
                  <p>
                    Besonders sensibel gehen wir mit vertraulichen Unterlagen
                    um. Akten und Datenträger werden nach DIN 66399 vernichtet,
                    entweder direkt vor Ort oder in versiegelten Behältern.
                    Festplatten zerstören wir grundsätzlich mechanisch.
                    IT-Infrastruktur und Serverräume demontieren wir
                    fachgerecht.
                  </p>
                </div>

                <div className="bg-primary/5 p-6 md:p-8 rounded-sm border border-primary/10 shadow-sm">
                  <p className="font-bold text-foreground text-xl">
                    Diskret, zuverlässig und ohne dass Ihre Nachbarbüros etwas
                    davon mitbekommen.
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Kostenfreies Angebot{" "}
                        <span className="ml-2 font-normal">→</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary rounded-md shadow-sm transition-transform hover:scale-105"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a9016967e20d627a9d3de.jpg"
                  alt="Gewerberäume und Büros"
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FIXED PRICE SECTION */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Image */}
              <div className="relative w-full order-2 lg:order-1">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform -rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901472499097121234fb.jpg"
                  alt="Festpreis. Keine Überraschungen."
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>

              {/* Right Column: Text */}
              <div className="space-y-8 order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Festpreis. Keine Überraschungen.
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Nach einer kostenlosen Besichtigung bekommen Sie einen
                    Kostenvoranschlag, der verbindlich ist. Kein Aufschlag,
                    keine versteckten Kosten, egal wie groß der Auftrag ist.
                  </p>
                  <p>
                    Besichtigungstermine bieten wir oft innerhalb von 24 Stunden
                    an. Der Preis steht fest, bevor wir den ersten Gegenstand
                    anrühren.
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Kostenfreies Angebot{" "}
                        <span className="ml-2 font-normal">→</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary rounded-md shadow-sm transition-transform hover:scale-105"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICE AREAS SECTION */}
        <section id="einsatzgebiete" className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-6">
                Unsere Einsatzgebiete in und um Berlin
              </h2>
              <p className="text-lg text-muted-foreground">
                Wir sind in ganz Berlin und im Umland für Sie im Einsatz. Wählen
                Sie Ihr Gebiet aus, um mehr zu erfahren.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Interactive Links */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                {[
                  { name: "Berlin", slug: "berlin" },
                  { name: "Pankow", slug: "pankow" },
                  { name: "Oberhavel", slug: "oberhavel" },
                  { name: "Marzahn-Hellersdorf", slug: "marzahn-hellersdorf" },
                  { name: "Treptow Köpenick", slug: "treptow-koepenick" },
                  { name: "Spandau", slug: "spandau" },
                  { name: "Reinickendorf", slug: "reinickendorf" },
                  { name: "Neukölln", slug: "neukoelln" },
                  { name: "Steglitz Zehlendorf", slug: "steglitz-zehlendorf" },
                  { name: "Märkisch Oderland", slug: "maerkisch-oderland" },
                  { name: "Charlottenburg", slug: "charlottenburg" },
                  { name: "Potsdam", slug: "potsdam" },
                  { name: "Oranienburg", slug: "oranienburg" },
                ].map((area, idx) => (
                  <a
                    key={idx}
                    href={`/${area.slug}`}
                    className="group flex flex-col items-center justify-center text-center gap-2 p-4 bg-background hover:bg-primary hover:text-primary-foreground rounded-sm border border-border transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    <MapPin className="h-6 w-6 text-primary group-hover:text-accent transition-colors" />
                    <span className="font-bold text-sm">{area.name}</span>
                  </a>
                ))}
              </div>

              {/* Map Visual - 2-Klick-Lösung für DSGVO-Konformität */}
              <div className="relative w-full rounded-sm overflow-hidden border border-border shadow-lg">
                <GoogleMapsEmbed
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d617689.2544599194!2d12.484348240355132!3d52.789037222350245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x23bf4a60473c084d%3A0x8a12d3b416c3d36a!2sMiros%20Haushaltsaufl%C3%B6sung!5e0!3m2!1sde!2sde!4v1771155121430!5m2!1sde!2sde"
                  title="Miros Haushaltsauflösung Einsatzgebiete"
                  height="500px"
                />
              </div>
            </div>
          </div>
        </section>

        {/* VALUE RECOGNITION SECTION */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Wertvolles erkennen, fair anrechnen
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Der alte Schmuck, die Münzsammlung, das antike Buffet:
                    Vieles hat mehr Wert als man denkt. Unser Team erkennt das
                    und rechnet es transparent gegen die Kosten der Entrümpelung
                    auf.
                  </p>
                  <p>
                    Das kann Ihre Rechnung deutlich senken. Manchmal entsteht
                    sogar eine Auszahlung. Die Wertermittlung ist kostenlos und
                    gehört bei uns zum Standard.
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Kostenfreies Angebot{" "}
                        <span className="ml-2 font-normal">→</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary rounded-md shadow-sm transition-transform hover:scale-105"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a9011ee187be68947e2b1.jpg"
                  alt="Wertvolles erkennen, fair anrechnen"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* PARKING & TRANSPORT SECTION */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Image */}
              <div className="relative w-full order-2 lg:order-1">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform -rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a659bec99fc9f3e99.jpg"
                  alt="Halteverbot, Transport, enge Treppenhäuser"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>

              {/* Right Column: Text */}
              <div className="space-y-8 order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Halteverbot, Transport, enge Treppenhäuser: Wir regeln das
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Parkplätze in Berlin zu finden ist schon im Alltag
                    schwierig. Für eine Entrümpelung braucht man eine
                    Halteverbotszone, und die beantragen wir für Sie, inklusive
                    Aufstellung der Schilder und Abholung danach. Sie müssen
                    sich um nichts kümmern.
                  </p>
                  <p>
                    Unsere Fahrzeuge sind für Berliner Verhältnisse ausgerüstet:
                    enges Treppenhaus, kein Aufzug, denkmalgeschützter Boden,
                    schweres Klavier. Wir haben die richtige Ausrüstung und die
                    Erfahrung, um auch schwierige Transporte sicher und ohne
                    Schäden zu erledigen.
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Kostenfreies Angebot{" "}
                        <span className="ml-2 font-normal">→</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary rounded-md shadow-sm transition-transform hover:scale-105"
                    asChild
                  >
                    <a href="tel:015253181061">
                      <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ABOUT / TRUST SECTION */}
        <section id="ueber-uns" className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 text-primary font-bold tracking-wide uppercase">
                  <Shield className="h-5 w-5" />
                  <span>Erfahrung, der Sie vertrauen können</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-bold text-foreground leading-tight">
                  Seit 5 Jahren steht Miro’s Haushaltsauflösung für Arbeit mit
                  Herz und Struktur.
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Über 500 erfolgreich betreute Objekte sprechen für echte
                    Erfahrung und eingespielte Prozesse. Von der ersten Anfrage
                    bis zur Übergabe läuft alles transparent, planbar und
                    persönlich betreut.
                  </p>
                  <p>
                    Ehrliche Kommunikation, termintreue Umsetzung und
                    sorgfältige Arbeit sorgen dafür, dass jeder Auftrag
                    professionell und respektvoll abgeschlossen wird.
                  </p>
                </div>

                <div className="flex items-center gap-6 pt-4">
                  <div className="flex items-center gap-4 bg-background p-4 rounded-sm border border-border shadow-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-8 h-8 shrink-0"
                    >
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    <div>
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className="h-5 w-5 fill-yellow-400 text-yellow-400"
                          />
                        ))}
                      </div>
                      <div className="text-sm font-bold text-foreground">
                        5.0 / 5.0 Top-Bewertungen
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -inset-4 bg-secondary rounded-sm transform rotate-3 z-0"></div>
                <img
                  src={optimizeImage(
                    "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c799.jpg",
                    800,
                  )}
                  alt="Professionelles Team bei der Arbeit"
                  className="relative z-10 w-full h-auto aspect-[4/3] object-cover rounded-sm shadow-xl"
                />
                <div className="absolute -bottom-8 -left-8 z-20 bg-primary text-primary-foreground p-6 rounded-sm shadow-xl max-w-xs hidden md:block">
                  <div className="font-heading font-bold text-4xl mb-1">
                    500+
                  </div>
                  <div className="text-sm font-medium opacity-90">
                    Erfolgreich betreute Objekte in Berlin & Umland
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS SECTION */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-6">
                So läuft eine Haushaltsauflösung bei uns ab
              </h2>
              <p className="text-lg text-muted-foreground">
                Schritt für Schritt einfach erklärt – von der ersten Anfrage bis
                zur besenreinen Übergabe.
              </p>
            </div>

            <div className="relative max-w-5xl mx-auto">
              {/* Vertical Line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-border transform md:-translate-x-1/2 hidden sm:block"></div>

              <div className="space-y-8 md:space-y-12">
                {[
                  {
                    title: "1. Kontaktaufnahme",
                    desc: "Kontaktieren Sie uns per Telefon, E-Mail oder über unser Formular. Wir besprechen kurz Ihr Anliegen und vereinbaren einen zeitnahen, kostenlosen Besichtigungstermin.",
                    img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eae7d476839065310.jpg",
                    cta: true,
                  },
                  {
                    title: "2. Besichtigungstermin",
                    desc: "Wir kommen zu Ihnen vor Ort, um uns ein genaues Bild vom Umfang der Haushaltsauflösung zu machen. Dabei klären wir alle Details und Ihre speziellen Wünsche.",
                    img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901472499097121234fb.jpg",
                  },
                  {
                    title: "3. Festpreisangebot",
                    desc: "Auf Basis der Besichtigung und eventueller Wertanrechnungen erstellen wir Ihnen ein verbindliches Festpreisangebot. Keine versteckten Kosten, volle Transparenz.",
                    img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c791.jpg",
                  },
                  {
                    title: "4. Haushaltsauflösung",
                    desc: "Am vereinbarten Termin räumt unser erfahrenes Team das Objekt professionell, diskret und zügig. Wir kümmern uns um alles, inklusive Halteverbotszonen und Demontage.",
                    img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a90167249909712123538.jpg",
                  },
                  {
                    title: "5. Entsorgung und Übergabe",
                    desc: "Wir trennen alle Materialien fachgerecht und entsorgen sie umweltfreundlich. Am Ende übergeben wir Ihnen die Räumlichkeiten besenrein.",
                    img: "https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a9011ae7d476839065366.jpg",
                  },
                ].map((step, idx) => (
                  <div
                    key={idx}
                    className={`relative flex flex-col sm:flex-row items-center gap-8 md:gap-16 ${idx % 2 === 1 ? "md:flex-row-reverse" : ""}`}
                  >
                    {/* Number Badge */}
                    <div className="absolute left-8 md:left-1/2 top-8 md:top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl border-4 border-background z-10 hidden sm:flex shadow-sm">
                      {idx + 1}
                    </div>

                    {/* Card */}
                    <div
                      className={`w-full sm:w-1/2 ${idx % 2 === 1 ? "md:pr-12 lg:pr-16" : "md:pl-12 lg:pl-16"} flex ${idx % 2 === 1 ? "justify-start" : "justify-end"}`}
                    >
                      <div className="bg-background rounded-sm shadow-md border border-border overflow-hidden w-full max-w-md relative">
                        {/* Mobile Number Badge */}
                        <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg border-2 border-background z-20 sm:hidden shadow-sm">
                          {idx + 1}
                        </div>

                        <div className="h-48 sm:h-56 overflow-hidden relative">
                          <img
                            src={step.img}
                            alt={step.title.substring(3)}
                            width="400"
                            height="250"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-6 md:p-8">
                          <h3 className="text-xl font-bold text-foreground mb-3">
                            {step.title.substring(3)}
                          </h3>
                          <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-6">
                            {step.desc}
                          </p>
                          {step.cta && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button className="w-full font-bold bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm">
                                  Jetzt Kontakt aufnehmen
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                                <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                                  <DialogHeader className="mb-5">
                                    <DialogTitle className="text-xl sm:text-2xl font-bold">
                                      Kostenloses Angebot anfordern
                                    </DialogTitle>
                                    <DialogDescription className="text-sm sm:text-base">
                                      Hinterlassen Sie uns Ihre Details und wir
                                      melden uns umgehend bei Ihnen.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <ContactForm />
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Empty space for the other side on desktop */}
                    <div className="hidden sm:block w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT & LOCATION SECTION */}
        <section id="kontakt" className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Info & Map */}
              <div className="space-y-10">
                <div>
                  <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6">
                    Kontaktieren Sie uns
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Hinterlassen Sie uns eine Nachricht oder rufen Sie direkt
                    an. Wir melden uns umgehend mit einem kostenlosen und
                    unverbindlichen Angebot.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">
                        Telefon & WhatsApp
                      </div>
                      <div className="flex flex-col gap-1">
                        <a
                          href="tel:015253181061"
                          className="text-muted-foreground hover:text-primary text-lg"
                        >
                          01525 3181061
                        </a>
                        <a
                          href="https://wa.me/4915253181061"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#15803d] hover:text-[#166534] text-base font-medium inline-flex items-center gap-1.5"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4 fill-current"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                          </svg>
                          Auf WhatsApp schreiben
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">Adresse</div>
                      <div className="text-muted-foreground text-lg">
                        Hauptstraße 43-44
                        <br />
                        13127 Berlin
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Inhaber: Miroslav Ozegovic
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">Öffnungszeiten</div>
                      <div className="text-muted-foreground text-lg">
                        Mo - Sa: 8:00 - 20:00 Uhr
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map Embed - 2-Klick-Lösung */}
                <div className="w-full rounded-sm overflow-hidden border border-border shadow-sm">
                  <GoogleMapsEmbed
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d617689.2544599194!2d12.484348240355132!3d52.789037222350245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x23bf4a60473c084d%3A0x8a12d3b416c3d36a!2sMiros%20Haushaltsaufl%C3%B6sung!5e0!3m2!1sde!2sde!4v1771155121430!5m2!1sde!2sde"
                    title="Miros Haushaltsauflösung Standort"
                    height="300px"
                  />
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background rounded-sm border border-border shadow-lg p-6 sm:p-8 md:p-10">
                <h3 className="text-2xl font-bold mb-6">
                  Kostenloses Angebot anfordern
                </h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left Column: Image */}
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform -rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901b72499097121235d0.jpg"
                  alt="Häufig gestellte Fragen"
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>

              {/* Right Column: FAQ Accordion */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                    Häufig gestellte Fragen
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Hier finden Sie Antworten auf die wichtigsten Fragen rund um
                    unsere Haushaltsauflösungen und Entrümpelungen in Berlin.
                  </p>
                </div>

                <Accordion
                  type="single"
                  collapsible
                  className="w-full bg-background rounded-sm border border-border p-2 shadow-sm"
                >
                  <AccordionItem value="item-1" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Wie schnell bekommen wir einen Termin?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed px-4 pb-4">
                      Besichtigungstermine sind oft innerhalb von 24 Stunden
                      möglich. Die eigentliche Entrümpelung findet meist in
                      derselben Woche statt. Bei dringenden Fällen, zum Beispiel
                      gerichtlich angeordnete Räumungen, organisieren wir auch
                      kurzfristigere Termine.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-2" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Was kostet eine Entrümpelung?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed px-4 pb-4">
                      Das hängt von Größe, Stockwerk und Zugänglichkeit ab. Eine
                      typische 3-Zimmer-Wohnung liegt zwischen 800 und 1.500
                      Euro. Wertvolle Gegenstände wie Antiquitäten oder Metalle
                      können den Preis reduzieren. Nach der kostenlosen
                      Besichtigung erhalten Sie einen verbindlichen Festpreis,
                      inklusive Halteverbot und Entsorgung, ohne Nachzahlungen.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-3" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Muss ich vorher irgendetwas tun?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed px-4 pb-4">
                      Nein. Sie müssen nur persönliche Dinge wie Dokumente,
                      Fotos oder Schmuck in Sicherheit bringen und uns sagen,
                      was weg soll. Den Rest übernehmen wir vollständig.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-4" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Übernehmen Sie auch die Entsorgung?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed px-4 pb-4">
                      Ja, komplett. Wir sortieren vor Ort: Verwertbares geht an
                      Second-Hand-Läden oder Recyclingbetriebe, Sondermüll wird
                      fachgerecht entsorgt, nur echter Restmüll landet in der
                      Verbrennung. Entsorgungsnachweise stellen wir auf Wunsch
                      aus.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-5" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Führen Sie auch Endreinigungen durch?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed px-4 pb-4">
                      Ja. Wir übergeben die Räume besenrein oder auf Wunsch
                      grundgereinigt.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-6" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Erkennen Sie Wertgegenstände?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed px-4 pb-4">
                      Ja. Unser Team erkennt Antiquitäten, Sammlerstücke und
                      andere Wertsachen und rechnet sie transparent gegen die
                      Kosten an. Das kann Ihre Rechnung spürbar senken.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-7" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Was passiert, wenn beim Transport etwas beschädigt wird?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed px-4 pb-4">
                      Wir sind vollständig betriebshaftpflichtversichert. Sollte
                      trotz aller Sorgfalt ein Schaden entstehen, übernimmt
                      unsere Versicherung die Kosten. Die
                      Versicherungsbestätigung können Sie vor Arbeitsbeginn
                      einsehen.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col md:flex-row bg-primary rounded-sm overflow-hidden shadow-2xl border border-border">
                {/* Left Side: Text and CTAs */}
                <div className="flex-1 p-6 sm:p-10 md:p-12 flex flex-col justify-center text-left">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-primary-foreground mb-8 leading-tight">
                    Sie haben noch weitere Fragen?
                    <br />
                    Melden Sie sich gerne bei uns.
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          size="lg"
                          className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                        >
                          Kostenfreies Angebot{" "}
                          <span className="ml-2 font-normal">→</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                        <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                          <DialogHeader className="mb-5">
                            <DialogTitle className="text-xl sm:text-2xl font-bold">
                              Kostenloses Angebot anfordern
                            </DialogTitle>
                            <DialogDescription className="text-sm sm:text-base">
                              Hinterlassen Sie uns Ihre Details und wir melden
                              uns umgehend bei Ihnen.
                            </DialogDescription>
                          </DialogHeader>
                          <ContactForm />
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-14 px-8 text-lg font-bold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-md shadow-md transition-transform hover:scale-105"
                      asChild
                    >
                      <a href="tel:015253181061">
                        <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Right Side: Image */}
                <div className="w-full md:w-2/5 min-h-[300px] md:min-h-full relative">
                  <img
                    src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901bb2e956bc24f8ce86.jpg"
                    alt="Haben Sie weitere Fragen?"
                    width="600"
                    height="800"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Index;
